import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaApple,
  FaBalanceScale,
  FaArrowRight,
  FaCarSide,
  FaClock,
  FaDownload,
  FaEnvelope,
  FaFileAlt,
  FaGasPump,
  FaGlobeAfrica,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRoute,
  FaShieldAlt,
  FaTrashAlt,
  FaWallet,
} from 'react-icons/fa';
import logoFull from '../images/logo.svg';
import logoBlue from '../images/whe2-logo-header.svg';
import appInterfaceScreenshot from '../images/whe2-app-interface-ios.png';
import downloadScenicRoad from '../images/whe2-scenic-road.jpg';
import googlePlayIcon from '../images/google-play-icon.png';
import heroDog from '../images/whe2-road-trip-dog.jpg';
import '../css/landing.css';
import Seo from '../components/Common/Seo';
import { routeGuides } from '../data/routeGuides';

const IOS_STORE_URL = process.env.REACT_APP_IOS_STORE_URL || 'https://apps.apple.com/us/app/whe2/id6761093738';
const ANDROID_STORE_URL = process.env.REACT_APP_ANDROID_STORE_URL || 'https://play.google.com/store/apps/details?id=com.whe2.app&pcampaignid=web_share';

const storeButtons = [
  {
    key: 'ios',
    label: 'App Store',
    sublabel: 'Download on the',
    icon: <FaApple className="store-icon-apple" />,
    href: IOS_STORE_URL,
  },
  {
    key: 'android',
    label: 'Google Play',
    sublabel: 'GET IT ON',
    icon: <img src={googlePlayIcon} alt="" className="store-icon-image store-icon-image-google-play" />,
    href: ANDROID_STORE_URL,
  }
];

const featureCards = [
  {
    icon: <FaRoute />,
    accent: 'primary',
    title: 'Choose the Best Route',
    description: 'Compare options quickly with map views and clear fastest or cheapest choices.'
  },
  {
    icon: <FaWallet />,
    accent: 'primary',
    title: 'Know Your Trip Cost Upfront',
    description: 'See fuel, toll and total trip costs before you leave.'
  },
  {
    icon: <FaGasPump />,
    accent: 'success',
    title: 'Accurate Fuel Estimates',
    description: 'Use your vehicle details and saved preferences for more realistic fuel costs.'
  },
  {
    icon: <FaClock />,
    accent: 'success',
    title: 'Plan Your Trip Your Way',
    description: 'Choose when to leave or arrive with flexible date and time planning.'
  },
  {
    icon: <FaInfoCircle />,
    accent: 'support',
    title: 'Stay on Schedule',
    description: 'Set reminders that fit your trip so you are ready when it is time to go.'
  },
  {
    icon: <FaCarSide />,
    accent: 'support-alt',
    title: 'Manage Your Trips',
    description: 'Keep planned journeys, upcoming trips and summaries together in one place.'
  },
  {
    icon: <FaMapMarkerAlt />,
    accent: 'utility',
    title: 'See What’s on Your Trip',
    description: 'Check tollgates, breakdowns and fuel stops before you get on the road.'
  },
  {
    icon: <FaGlobeAfrica />,
    accent: 'local',
    title: 'Built for South African Roads',
    description: 'Made for local roads, toll routes and practical trip planning for South African drivers.'
  }
];

const interfaceCards = [
  {
    icon: <FaMapMarkerAlt />,
    title: 'Enter Your Route',
    description: 'Enter your starting point and destination to build a South African driving route.'
  },
  {
    icon: <FaRoute />,
    title: 'Compare Better Routes',
    description: 'Review route options with clearer cost, distance, travel time and road-condition trade-offs.'
  },
  {
    icon: <FaClock />,
    title: 'Plan Ahead',
    description: 'Schedule the trip for later with departure or arrival time planning built into the flow.'
  },
  {
    icon: <FaInfoCircle />,
    title: 'Get Trip Guidance',
    description: 'See reminders, road-condition context and clearer trip expectations before you leave.'
  }
];

const steps = [
  {
    number: '01',
    icon: <FaMapMarkerAlt />,
    accent: 'step-start',
    title: 'Enter Your Route',
    description: 'Enter your starting point and destination for the South African road trip you actually want to make.'
  },
  {
    number: '02',
    icon: <FaCarSide />,
    accent: 'step-customisation',
    title: 'Set Vehicle Details',
    description: 'Choose vehicle type, fuel type, class and consumption so the route and fuel calculation match reality.'
  },
  {
    number: '03',
    icon: <FaRoute />,
    accent: 'step-analysis',
    title: 'Compare Options',
    description: 'Review route choices with clearer cost summaries, road-condition context, distance and travel time before you decide.'
  },
  {
    number: '04',
    icon: <FaClock />,
    accent: 'step-success',
    title: 'Schedule And Go',
    description: 'Plan the trip for later, set reminders and keep upcoming journeys ready when you need them.'
  }
];

const supportCards = [
  {
    icon: <FaShieldAlt />,
    accent: 'blue',
    title: 'Privacy Policy',
    description: 'Learn how Whe2 handles and protects your data.',
    href: '/legal/privacy-policy.html',
  },
  {
    icon: <FaFileAlt />,
    accent: 'violet',
    title: 'Terms & Conditions',
    description: 'Read the legal terms that govern use of the service.',
    href: '/legal/terms-and-conditions.html',
  },
  {
    icon: <FaTrashAlt />,
    accent: 'red',
    title: 'Account Deletion',
    description: 'Request deletion of your account and associated data.',
    href: '/legal/account-deletion.html',
  },
  {
    icon: <FaBalanceScale />,
    accent: 'legal',
    title: 'Legal Centre',
    description: 'Open the full legal index and core policy documents in one place.',
    href: '/legal.html',
  },
];

const featuredGuides = routeGuides.slice(0, 4);

const LandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const getPreferredStoreUrl = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return '';
    }

    const userAgent = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    const isAndroid = /Android/i.test(userAgent);
    const isIPhone = /iPhone/i.test(userAgent);
    const isIPad = /iPad/i.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1);
    const isIPod = /iPod/i.test(userAgent);

    if (isAndroid) {
      return ANDROID_STORE_URL;
    }

    if (isIPhone || isIPad || isIPod) {
      return IOS_STORE_URL;
    }

    return '';
  };

  const scrollToSection = (targetId, { updateHash = false } = {}) => {
    const targetElement = document.getElementById(targetId);

    if (!targetElement) {
      return;
    }

    const headerOffset = 96;
    const targetTop = targetElement.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });

    if (updateHash) {
      window.history.replaceState(null, '', targetId === 'top' ? '/' : `/#${targetId}`);
    }
  };

  const handleSectionClick = (targetId, { closeMenu = false } = {}) => (event) => {
    event.preventDefault();
    if (closeMenu) {
      setIsMenuOpen(false);
    }
    scrollToSection(targetId, { updateHash: true });
  };

  const handleDownloadClick = ({ closeMenu = false } = {}) => (event) => {
    const preferredStoreUrl = getPreferredStoreUrl();

    if (preferredStoreUrl) {
      event.preventDefault();
      if (closeMenu) {
        setIsMenuOpen(false);
      }
      window.location.href = preferredStoreUrl;
      return;
    }

    handleSectionClick('download', { closeMenu })(event);
  };

  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Whe2',
    url: 'https://www.whe2.com/',
    description: 'South Africa route planning app with fuel cost and toll estimates.'
  };

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.16 }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const targetFromHash = location.hash ? location.hash.replace('#', '') : '';
    const targetFromState = location.state?.scrollTarget || '';
    const targetId = targetFromHash || targetFromState;

    if (!targetId) {
      return;
    }

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        scrollToSection(targetId, { updateHash: true });

        if (targetFromState) {
          navigate(
            {
              pathname: location.pathname,
              hash: `#${targetId}`,
            },
            { replace: true, state: null }
          );
        }
      }, 0);
    });
  }, [location.hash, location.pathname, location.state, navigate]);

  return (
    <div className="landing-page">
      <Seo
        title="Whe2 | South Africa Trip Planner, Fuel Cost and Toll Calculator"
        description="Plan South African trips with route comparison, fuel and toll estimates, reminders and scheduled trip planning."
        path="/"
        jsonLd={seoJsonLd}
      />
      <header className={`site-header ${isScrolled ? 'is-scrolled' : ''}`}>
        <div className="container header-row">
          <a className="logo" href="#top" aria-label="Whe2 home" onClick={handleSectionClick('top')}>
            <img src={logoBlue} alt="Whe2" className="logo-full-image" />
          </a>

          <nav className={`nav ${isMenuOpen ? 'is-open' : ''}`} aria-label="Primary navigation">
            <a href="#features" onClick={handleSectionClick('features', { closeMenu: true })}>Features</a>
            <a href="#how" onClick={handleSectionClick('how', { closeMenu: true })}>How It Works</a>
            <a href="#support" onClick={handleSectionClick('support', { closeMenu: true })}>Support</a>
            <a href="#download" className="nav-download" onClick={handleDownloadClick({ closeMenu: true })}>
              <span>Download App</span>
              <FaDownload />
            </a>
          </nav>

          <button
            className="menu-btn"
            type="button"
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((prev) => !prev)}
          >
            ☰
          </button>
        </div>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="container hero-grid">
            <div className="hero-copy reveal">
              <div className="hero-pill">
                <FaGlobeAfrica />
                <span>South Africa&apos;s Smarter Route Planner</span>
              </div>

              <h1>
                Plan Your Journey With <span>Confidence</span>
              </h1>

              <p className="lead">
                Get clearer fuel, toll and travel-time estimates for South African road trips. Compare route options, plan trips ahead and use reminders to stay ready before you leave.
              </p>

              <div className="hero-stats">
                <div>
                  <strong>Fuel + tolls</strong>
                  <span>One route cost view</span>
                </div>
                <div>
                  <strong>Trip reminders</strong>
                  <span>Plan ahead with confidence</span>
                </div>
                <div>
                  <strong>Upcoming trips</strong>
                  <span>Keep future journeys ready</span>
                </div>
              </div>

              <div className="cta-row">
                {storeButtons.map((button) => (
                  button.href ? (
                      <a
                        key={button.key}
                        className={`store-btn store-btn-${button.key}`}
                        href={button.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={button.label}
                    >
                      <span className="store-icon">{button.icon}</span>
                      <span className="store-meta">
                        <small>{button.sublabel}</small>
                        <strong>{button.label}</strong>
                      </span>
                    </a>
                  ) : (
                    <div key={button.key} className={`store-btn store-btn-${button.key} is-disabled`} aria-disabled="true">
                      <span className="store-icon">{button.icon}</span>
                      <span className="store-meta">
                        <small>Store link coming soon</small>
                        <strong>{button.label}</strong>
                      </span>
                    </div>
                  )
                ))}
              </div>
            </div>

            <div className="hero-visual reveal" aria-hidden="true">
              <div className="hero-chat-board">
                <div className="hero-chat-header">
                  <div className="hero-chat-avatar">
                    <FaMapMarkerAlt />
                  </div>
                  <div className="hero-chat-contact">
                    <strong>Road Trip Group</strong>
                    <span>Weekend crew</span>
                  </div>
                  <FaPhoneAlt className="hero-chat-call" />
                </div>

                <div className="hero-chat-body">
                  <span className="hero-chat-time">23:50</span>
                  <span className="destination-bubble hero-message-primary">Whe2?</span>
                  <span className="destination-bubble hero-message-secondary">Cape Town?</span>
                  <span className="hero-chat-time hero-chat-time-right">23:52</span>

                  <div className="hero-destination-card">
                    <div className="hero-destination-image">
                      <img src={heroDog} alt="Dog in sunglasses leaning out of a car window." className="hero-destination-photo" />
                    </div>
                    <div className="hero-destination-meta hero-destination-meta-centered">
                      <strong>LET'S GO!</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="features-section" id="features">
          <div className="container">
            <div className="section-head section-head-center reveal">
              <p className="section-kicker">Powerful Features</p>
              <h2>Everything You Need For <span>Smarter Travel</span></h2>
              <p>
                Whe2 combines South Africa route planning with practical fuel, toll, reminder and road-condition visibility so you can make better road-trip decisions before departure.
              </p>
            </div>

            <div className="feature-grid">
              {featureCards.map((feature) => (
                <article className="feature-card reveal" key={feature.title}>
                  <div className={`feature-icon accent-${feature.accent}`}>{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="interface-section">
          <div className="container interface-grid">
            <div className="interface-visual reveal">
              <div className="interface-phone-frame">
                <img
                  src={appInterfaceScreenshot}
                  alt="Whe2 iOS app screen showing route entry and vehicle settings."
                  className="interface-phone-image"
                />
              </div>
            </div>

            <div className="interface-copy reveal">
              <p className="section-kicker on-dark">App Interface</p>
              <h2>A Simple Interface Designed For Real Planning</h2>
              <p>
                Everything you need for planning a real trip is kept in one practical flow. No clutter, no guesswork, just a clearer way to compare routes, schedule ahead and understand what the trip will cost.
              </p>

              <div className="interface-cards">
                {interfaceCards.map((item) => (
                  <article className="interface-card" key={item.title}>
                    <div className="interface-icon">{item.icon}</div>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </article>
                ))}
              </div>

              <a className="interface-cta" href="#download" onClick={handleDownloadClick()}>
                Download App <FaDownload />
              </a>
            </div>
          </div>
        </section>

        <section className="how-section" id="how">
          <div className="container">
            <div className="section-head section-head-center reveal">
              <p className="section-kicker">How It Works</p>
              <h2>Get Started In <span>4 Simple Steps</span></h2>
              <p>
                Planning a South African road trip with clearer cost estimates, reminders and route comparison should be straightforward.
              </p>
            </div>

            <div className="how-grid how-grid-four">
              {steps.map((step) => (
                <article className="how-step-card reveal" key={step.number}>
                  <span className="step-ghost">{step.number}</span>
                  <div className={`step-icon ${step.accent}`}>{step.icon}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>


        <section className="download-section" id="download">
          <div className="container download-grid">
            <div className="download-copy reveal">
              <p className="section-kicker on-dark">Available On iOS & Android</p>
              <h2>Ready To Start Your <span>Journey</span>?</h2>
              <p>
                Download Whe2 and use a South Africa trip planner built to help you compare routes, schedule journeys and understand fuel and toll costs in one place.
              </p>

              <div className="download-store-block">
                <p className="download-store-heading">Start Planning Your Next Trip Today</p>
                <div className="cta-row download-store-actions">
                {storeButtons.map((button) => (
                  button.href ? (
                      <a
                        key={`download-${button.key}`}
                        className={`store-btn store-btn-${button.key} download-store-btn`}
                        href={button.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={button.label}
                    >
                      <span className="store-icon">{button.icon}</span>
                      <span className="store-meta">
                        <small>{button.sublabel}</small>
                        <strong>{button.label}</strong>
                      </span>
                    </a>
                  ) : (
                    <div key={`download-${button.key}`} className={`store-btn store-btn-${button.key} is-disabled download-store-btn`} aria-disabled="true">
                      <span className="store-icon">{button.icon}</span>
                      <span className="store-meta">
                        <small>Store link coming soon</small>
                        <strong>{button.label}</strong>
                      </span>
                    </div>
                  )
                ))}
                </div>
              </div>

              <div className="download-stats">
                <div className="download-stat stat-road">
                  <strong><span className="download-stat-dot" />Road-First</strong>
                  <span>Built for South African Route Planning</span>
                </div>
                <div className="download-stat stat-cost">
                  <strong><span className="download-stat-dot" />Cost-Aware</strong>
                  <span>Fuel And Toll Visibility In One Flow</span>
                  <small className="download-status-row"><span className="status-good">Best Route</span><span className="status-mid">Alternative</span><span className="status-bad">Expensive</span></small>
                </div>
                <div className="download-stat stat-planner">
                  <strong><span className="download-stat-dot" />Trip-Ready</strong>
                  <span>Schedule trips, set reminders and keep upcoming plans ready</span>
                </div>
              </div>
            </div>

            <div className="download-visual reveal">
              <div className="download-phone-shell">
                <img
                  src={downloadScenicRoad}
                  alt="Scenic South African road used as a travel-planning visual."
                  className="download-phone-image"
                  width="719"
                  height="1320"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="support-panel support-guides-section">
          <div className="container">
            <div className="guides-topbar reveal">
              <div className="guides-copy">
                <h2>Popular <span>South Africa Guides</span></h2>
                <p>
                  Browse practical route guides for major South African trips, with quick context on fuel, tolls, distance and route-planning trade-offs.
                </p>
              </div>
              <a href="/guides" className="guides-hub-link" target="_blank" rel="noreferrer">
                View All Guides <FaArrowRight />
              </a>
            </div>

            <div className="support-cards support-guides-grid">
              {featuredGuides.map((guide) => (
                <article className="support-card guide-card reveal" key={guide.slug}>
                  <h3>{guide.shortTitle}</h3>
                  <p>{guide.description}</p>
                  <a href={guide.path} className="support-link guide-link" target="_blank" rel="noreferrer">
                    Open Guide <FaArrowRight />
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="support-panel support-help-section" id="support">
          <div className="container">
            <div className="section-head section-head-center reveal support-head">
              <p className="section-kicker">Support</p>
              <h2>We&apos;re Here To <span>Help You</span></h2>
              <p>
                Find support, key product links and direct access to the legal centre in one place when you need help with route planning or your account.
              </p>
            </div>

            <div className="support-grid">
              <div className="support-stack">
                <div className="support-group-head reveal">
                  <p className="support-group-kicker">Quick links</p>
                  <h3>Support and legal access</h3>
                </div>
                <div className="support-cards support-primary-grid">
                  {supportCards.filter((item) => !['Privacy Policy', 'Terms & Conditions', 'Account Deletion'].includes(item.title)).map((item) => (
                    <article className="support-card reveal" key={item.title}>
                      <div className={`support-icon accent-${item.accent}`}>{item.icon}</div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <a href={item.href} className="support-link">
                        Learn More <FaArrowRight />
                      </a>
                    </article>
                  ))}
                </div>
              </div>

              <aside className="contact-card reveal">
                <div className="contact-head">
                  <div className="contact-icon">
                    <FaEnvelope />
                  </div>
                  <div>
                    <strong>Get In Touch</strong>
                    <span>Help With Route Planning, Product Questions And Account Support</span>
                  </div>
                </div>

                <p>
                  If you need help using Whe2, accessing the route planner or understanding trip-cost features, use the support channel below.
                </p>

                <a className="contact-email" href="mailto:support@whe2.com">
                  <FaEnvelope />
                  <span>support@whe2.com</span>
                </a>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <a className="logo footer-logo" href="#top" aria-label="Whe2 home" onClick={handleSectionClick('top')}>
              <img src={logoFull} alt="Whe2" className="logo-full-image" />
            </a>
            <p>
              Plan smarter South African road trips with clearer fuel, toll, reminder and route-planning visibility.
            </p>
            <a className="footer-cta" href="#download" onClick={handleDownloadClick()}>
              Download Whe2 <FaDownload />
            </a>
          </div>

          <div className="footer-columns">
            <div className="footer-column">
              <strong>Product</strong>
              <a href="#features" onClick={handleSectionClick('features')}>Features</a>
              <a href="#how" onClick={handleSectionClick('how')}>How It Works</a>
              <a href="/guides">Route Guides</a>
              <a href="#download" onClick={handleDownloadClick()}>Download</a>
            </div>

            <div className="footer-column">
              <strong>Support</strong>
              <a href="mailto:support@whe2.com">support@whe2.com</a>
              <a href="#download" onClick={handleDownloadClick()}>Download App</a>
              <a href="/guides">Guides Hub</a>
            </div>

            <div className="footer-column">
              <strong>Legal</strong>
              <a href="/legal/privacy-policy.html">Privacy Policy</a>
              <a href="/legal/terms-and-conditions.html">Terms & Conditions</a>
              <a href="/legal/account-deletion.html">Account Deletion</a>
            </div>
          </div>
        </div>

        <div className="container footer-bottom">
          <span>
            &copy; 2026 Whe2. All rights reserved.
            {' '}
            <a href="https://www.busany.co.za" target="_blank" rel="noreferrer">
              A Busany product.
            </a>
          </span>
          <div className="footer-bottom-links">
            <a href="/legal/privacy-policy.html">Privacy</a>
            <a href="/legal/terms-and-conditions.html">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
